/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useContext } from "react";

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { setupAxiosInterceptors } from "services/interceptor";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 PRO React components
import ChatBot from "components/ChatBot";

// Material Dashboard 2 PRO React examples
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React routes
import useAppRoutes from "routes";
import crudRoutes from "crud.routes";

// Material Dashboard 2 PRO React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  setOpenChatBot,
  AuthContext,
} from "context";

import { getPermissions } from "config/Permissions";

// Images
import brandWhite from "assets/images/logo.png";
import brandDark from "assets/images/logo.png";
import ProtectedRoute from "examples/ProtectedRoute";
import Login from "auth/login";
import Register from "auth/register";
import ForgotPassword from "auth/forgot-password";
import ResetPassword from "auth/reset-password";
import AuthService from "services/auth-service";
import CourseContent from "layouts/pages/course-content";
import SamplePage from "layouts/pages/sample-page";
import CertificatePage from "layouts/pages/my-certificates/certificate";
import PublicCourseContent from "layouts/pages/PublicCourseContent";
import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";


export default function App({ ability }) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    openChatBot,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname, search } = useLocation();
  const location = useLocation();

  const { getCurrentUser } = useContext(AuthContext);

  const appRoutes = useAppRoutes();

  useEffect(() => {
    const start_time = Date.now();
    const getUserId = async () => {

      try {
        return await getCurrentUser();
      } catch (error) {
        console.error("Failed to get user id", error);
        return "";
      }
    }
    getUserId().then((user) => {
      analytics.track(events.APP_VISITED, {
        start_time: start_time,
        user_id: user,
      });
    });
    return () => {
      const end_time = Date.now();
      getUserId().then((user) => {
        analytics.track(events.APP_LEFT, {
          start_time: start_time,
          end_time: end_time,
          user_id: user,
        });
      });
    };
  }, []);

  useEffect(() => {
    if (openChatBot) {
      setOpenChatBot(dispatch, false);
    }
  }, [location]);

  const authContext = useContext(AuthContext);
  const [userDetails, setUserDetails] = useState({ name: "", image: "", role: "" });

  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    setIsDemo(false);
  }, []);

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate();
  setupAxiosInterceptors(() => {
    console.log("INTERCEPTOR TRIGGERED", { pathname, search, err });
    if (pathname.includes("/course-information") || pathname.includes("/reset-password")) return;
    authContext.logout();
    navigate("/auth/login");
  });

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (!authContext.isAuthenticated) return;
    (async () => {
      const id = await authContext.getCurrentUser();
      const response = await AuthService.getProfile();
      const role = await authContext.getRole();

      setUserDetails({
        name: response.data.attributes.name,
        image: response.data.attributes.profile_image,
        role: role,
      });
      const rules = await getPermissions(id);
      ability.update(rules);
    })();
  }, [authContext.isAuthenticated]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.key === "course-information") {
        return (
          <Route
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        let allowedRoute = true;
        if (route.visibility !== undefined && userDetails.role !== "" && route.visibility === "admin" && userDetails.role !== "admin") {
          allowedRoute = false;
        }

        return (
          <Route
            path={route.route}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated && allowedRoute}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        );
      }

      return null;
    });


  const getCrudRoutes = (allRoutes) => {

    return allRoutes.map((route) => {

      // Directly render "course-information" without authentication
      if (route.key === "course-information") {
        return (
          <Route
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      if (route.collapse) {
        return getCrudRoutes(route.collapse);
      }

      if (route.route) {

        let allowedRoute = true;

        if (route.visibility !== undefined && userDetails.role !== "" && route.visibility === "admin" && userDetails.role !== "admin") {
          allowedRoute = false;
        }
        return (
          <Route
            path={route.route}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated && allowedRoute}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        );
      }
      return null;
    });
  };


  // code to make a page public
  if (pathname.includes("/sample-page")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <SamplePage />
      </ThemeProvider>
    );
  } else if (pathname.includes("/showCertificate")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <CertificatePage />
      </ThemeProvider>
    );
  }
  else if (pathname.includes("/reset-password")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <ResetPassword />
      </ThemeProvider>
    );
  }

  else if (pathname.includes("/auth/login")) {

    const params = new URLSearchParams(search);
    const redirectTo = params.get("redirect") || "/home-page";
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <Login redirectTo={redirectTo} />
      </ThemeProvider>
    );
  }
  else if (pathname.includes("/auth/register")) {

    const params = new URLSearchParams(search);
    const redirectTo = params.get("redirect") || "/home-page";
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <Register
          redirectTo={redirectTo}
        />
      </ThemeProvider>
    );
  }
  else if (pathname.includes("/forgot-password")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <ForgotPassword />
      </ThemeProvider>
    );
  }

  // code to make a page public
  if (pathname.includes("/course-information")) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <PublicCourseContent />
      </ThemeProvider>
    );
  }

  return (
    <>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName="QUSkillBridge"
              routes={appRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <ChatBot />
          </>
        )}
        {layout === "vr" && <ChatBot />}
        <Routes>
          <Route path="/course-content" element={<CourseContent />} />
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          {getRoutes(appRoutes)}
          {getCrudRoutes(crudRoutes)}
          <Route path="*" element={<Navigate to="/home-page" replace />} />
        </Routes>
      </ThemeProvider>
    </>
  );
}
